import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../context/AuthContext';
import ShieldLogo from '../components/ShieldLogo';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const from = location.state?.from || '/dashboard';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await login(email, password);
      const role = data?.user?.role;
      if (role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <Link
            to="/"
            data-testid="login-logo-link"
            className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
          >
            <ShieldLogo size={24} />
            <span
              className="text-2xl font-semibold"
              style={{ fontFamily: 'Instrument Serif, serif' }}
            >
              Verilock
            </span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 py-12 sm:py-16">
        <div className="w-full max-w-md">
          <h1
            className="text-3xl sm:text-4xl font-normal mb-2 leading-tight"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Welcome back
          </h1>
          <p className="text-sm sm:text-base text-[#1a1a1a]/70 mb-6 sm:mb-8">
            Sign in to your Verilock account.
          </p>

          {error && (
            <div
              data-testid="login-error-message"
              className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm mb-6"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-base font-normal text-[#1a1a1a]">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border-[#e0ddd8] mt-2"
                placeholder="you@company.com"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-base font-normal text-[#1a1a1a]">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="login-forgot-password-link"
                  className="text-sm text-[#0a5c44] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <PasswordInput
                id="password"
                data-testid="login-password-input"
                testIdToggle="login-password-toggle"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border-[#e0ddd8] mt-2"
                placeholder="••••••••"
                required
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#1a1a1a] text-white hover:bg-[#2a2a2a] transition-colors h-11"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>

          <p className="text-sm text-[#1a1a1a]/60 mt-8 text-center">
            Don't have an account?{' '}
            <Link to="/wizard" className="text-[#0a5c44] hover:underline">
              Start the compliance wizard
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
